import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { TRUST_STATS } from "../data/site";

const lineReveal = {
  hidden: { y: "112%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.25 + i * 0.16, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const fgY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col overflow-hidden bg-brand-coal">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(215,25,32,0.09),transparent_55%)]" />
      <div className="absolute inset-0 noise-overlay opacity-[0.07] pointer-events-none" />

      <div className="relative flex-1 flex items-center max-w-7xl mx-auto w-full px-5 sm:px-8 pt-32 pb-16">
        <div className="grid lg:grid-cols-12 gap-12 items-center w-full">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="flex items-center gap-3"
            >
              <span className="h-[2px] w-12 bg-brand-red" />
              <span className="text-xs sm:text-sm font-semibold tracking-[0.3em] uppercase text-white/70">
                Galvanized Steel Door & Window Frames
              </span>
            </motion.div>

            <h1 className="mt-7 font-display font-semibold uppercase leading-[0.98] tracking-tight text-[13vw] sm:text-7xl lg:text-[5.2rem]">
              <span className="block overflow-hidden pb-1">
                <motion.span custom={0} variants={lineReveal} initial="hidden" animate="show" className="block text-white">
                  Steel Door Frames
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-2">
                <motion.span custom={1} variants={lineReveal} initial="hidden" animate="show" className="block text-white">
                  Built for <span className="text-brand-red">Strength.</span>
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.75 }}
              className="mt-7 max-w-xl text-base sm:text-lg text-white/60 leading-relaxed"
            >
              Precision-engineered galvanized steel door and window frames for homes,
              offices, commercial and industrial construction.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-get-quote-btn"
                className="group inline-flex items-center gap-3 bg-brand-red text-white text-sm font-semibold tracking-[0.14em] uppercase px-8 py-4 hover:bg-white hover:text-brand-ink transition-colors duration-300"
              >
                Get a Quote
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" />
              </Link>
              <Link
                to="/products"
                data-testid="hero-view-products-btn"
                className="group inline-flex items-center gap-3 border border-white/30 text-white text-sm font-semibold tracking-[0.14em] uppercase px-8 py-4 hover:border-brand-red hover:text-brand-red transition-colors duration-300"
              >
                View Products
              </Link>
            </motion.div>
          </div>

          <motion.div style={{ y: fgY }} className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="absolute inset-0 bg-brand-red/20 blur-[110px] rounded-full scale-75" />
              <img
                src="/brand/hero-frames.jpg"
                alt="MODORS galvanized steel door frames and window frames"
                className="relative w-full max-h-72 sm:max-h-96 lg:max-h-[560px] object-cover object-top [mask-image:linear-gradient(to_bottom,black_90%,transparent)]"
              />
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.15 }}
        className="relative border-t border-white/15"
        data-testid="hero-trust-stats"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-3 divide-x divide-white/15">
          {TRUST_STATS.map((s) => (
            <div key={s.top} className="py-6 px-2 sm:px-6 first:pl-0">
              <div className="text-[10px] sm:text-xs font-semibold tracking-[0.28em] text-brand-red uppercase">
                {s.top}
              </div>
              <div className="mt-1.5 font-display text-lg sm:text-2xl font-medium uppercase tracking-wide text-white">
                {s.value}
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
